"use client";

import { useCallback, useEffect, useRef } from "react";
import { useEditorStoreApi, useTemporalStore } from "./context";
import { useActiveTextEditorId } from "./hooks";
import {
    canvasEqual,
    partializeCanvas,
    recordCanvasStep,
    redoCanvas,
    undoCanvas,
    type CanvasSnapshot,
} from "./canvasHistory";

/** Undo/redo wired to the canvas-only zundo history (see canvasHistory.ts). */
export function useUndoRedo() {
    const store = useEditorStoreApi();
    const canUndo = useTemporalStore((s) => s.pastStates.length > 0);
    const canRedo = useTemporalStore((s) => s.futureStates.length > 0);

    const undo = useCallback(() => undoCanvas(store), [store]);
    const redo = useCallback(() => redoCanvas(store), [store]);
    const clear = useCallback(() => {
        store.temporal.getState().clear();
    }, [store]);

    return { canUndo, canRedo, undo, redo, clear };
}

/**
 * Global Cmd/Ctrl+Z (undo) and Cmd/Ctrl+Shift+Z / Cmd+Y (redo). While a text
 * element is being edited, Tiptap owns undo, so we don't intercept.
 */
export function useUndoRedoShortcuts() {
    const store = useEditorStoreApi();
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const mod = e.metaKey || e.ctrlKey;
            if (!mod) return;
            const key = e.key.toLowerCase();
            if (key !== "z" && key !== "y") return;
            if (store.getState().activeTextEditorId) return;
            const target = e.target as HTMLElement | null;
            if (
                target &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.isContentEditable)
            )
                return;
            e.preventDefault();
            if (key === "y" || (key === "z" && e.shiftKey)) redoCanvas(store);
            else undoCanvas(store);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [store]);
}

/**
 * Coalesces a whole text-edit session into ONE history step. While a text
 * element is focused we pause history: Tiptap owns in-editor undo, and the
 * per-keystroke store commits would otherwise each become a step. On exit we
 * record a single step from the snapshot captured when editing began. Mount
 * once (in the editor root).
 */
export function useTextEditHistory() {
    const store = useEditorStoreApi();
    const activeTextEditorId = useActiveTextEditorId();
    const beforeRef = useRef<CanvasSnapshot | null>(null);
    const activeRef = useRef<string | null>(null);

    const finish = useCallback(() => {
        store.temporal.getState().resume();
        const before = beforeRef.current;
        beforeRef.current = null;
        if (!before) return;
        // The text element flushed its final HTML to the store before this runs
        // (child effect fires before this parent effect).
        const after = partializeCanvas(store.getState());
        if (!canvasEqual(before, after)) recordCanvasStep(store, before);
    }, [store]);

    useEffect(() => {
        const prev = activeRef.current;
        const next = activeTextEditorId;
        if (prev === next) return;

        if (prev !== null) finish();
        if (next !== null) {
            beforeRef.current = partializeCanvas(store.getState());
            store.temporal.getState().pause();
        }
        activeRef.current = next;
    }, [activeTextEditorId, store, finish]);

    // Safety net: unmounted mid-edit (e.g. navigating away) still records.
    useEffect(
        () => () => {
            if (activeRef.current !== null) finish();
        },
        [finish]
    );
}
